import type { ScaffoldFirstInference } from '@volley/crucible/core/index.js';

/**
 * Runtime validator for a `ScaffoldFirstInference` (A2.3b). The chip's data
 * arrives via React-Router route state, which is `unknown` — a crafted nav or a
 * future refactor could hand us a malformed object. Validate the union (outcome
 * + confidence + the branch-specific fields) at the boundary so a bad payload
 * collapses to "no chip" rather than rendering a broken pill. A type guard so
 * the caller gets a properly-narrowed `ScaffoldFirstInference`.
 */
export function isScaffoldFirstInference(value: unknown): value is ScaffoldFirstInference {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  // Confidence must be a FINITE number — reject NaN / Infinity / non-number so a
  // garbage payload never renders "NaN%" or an out-of-range pill. Range is
  // branch-specific (genre `(0, 1]`, sandbox `[0, 1]` — 0 on no-match/error).
  if (typeof v.confidence !== 'number' || !Number.isFinite(v.confidence)) return false;
  if (v.outcome === 'genre') {
    // A confident match: non-null preforge/genre + the matched candidate name.
    return (
      v.confidence > 0 &&
      v.confidence <= 1 &&
      typeof v.preforge === 'string' &&
      typeof v.genre === 'string' &&
      typeof v.candidate === 'string'
    );
  }
  if (v.outcome === 'sandbox') {
    // The floor: null preforge/genre, a known floorReason, candidate string|null.
    return (
      v.confidence >= 0 &&
      v.confidence <= 1 &&
      v.preforge === null &&
      v.genre === null &&
      (v.candidate === null || typeof v.candidate === 'string') &&
      (v.floorReason === 'below-threshold' ||
        v.floorReason === 'no-match' ||
        v.floorReason === 'error')
    );
  }
  return false;
}

/**
 * Gate for the preview genre chip (A2.3b). Show it ONLY when scaffold-first is
 * active (`scaffoldFirst && livePreview`, resolved by `useFeatureFlags`) AND
 * this session actually carried an inferred-genre outcome from create. A pure
 * predicate (mirrors `shouldAutoOpenPreview`) so the gate is unit-testable
 * without mounting the whole Workspace. The type guard lets the caller render
 * `<ScaffoldFirstGenreChip inference={...} />` without a re-narrow.
 */
export function shouldShowGenreChip(
  scaffoldFirstActive: boolean,
  inference: ScaffoldFirstInference | null,
): inference is ScaffoldFirstInference {
  return scaffoldFirstActive && inference !== null;
}

/**
 * Live-preview scaffold-first genre chip (A2.3b).
 *
 * Surfaces the inferred genre from create; optional `onChange` triggers a
 * lightweight start-over flow owned by the caller.
 */
export function ScaffoldFirstGenreChip({
  inference,
  onChange,
}: {
  inference: ScaffoldFirstInference;
  /** Optional re-cast handler. When supplied, a `· change` button is shown. */
  onChange?: () => void;
}): React.JSX.Element {
  const pct = Math.round(inference.confidence * 100);

  // Two visual tones: a confident genre match (sky) vs the sandbox floor
  // (amber — same "heads-up, not an error" tone as the install/interview chips).
  const isGenre = inference.outcome === 'genre';

  const label = isGenre ? (inference.genre ?? 'genre') : 'sandbox';

  // Tooltip detail — the confidence + (for the floor) why it fell through.
  const title = isGenre
    ? `Scaffolded a "${inference.genre}" game from your prompt ` +
      `(${pct}% confident). The build is refining it live.`
    : floorTitle(inference);

  const toneClass = isGenre ? 'bg-sky-500/20 text-sky-200' : 'bg-amber-500/20 text-amber-200';

  return (
    <span
      data-testid="scaffold-first-genre-chip"
      data-outcome={inference.outcome}
      title={title}
      // a11y: the genre/confidence/floor-reason detail lives in `title`, which
      // screen readers + keyboard users don't reliably get — mirror it on
      // `aria-label` so the full sentence is the chip's accessible name.
      aria-label={title}
      className={
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 ' +
        'text-[11px] font-medium shadow-sm backdrop-blur-sm ' +
        toneClass
      }
    >
      <span aria-hidden="true">{isGenre ? '🎮' : '🧪'}</span>
      <span>{label}</span>
      {onChange ? (
        <button
          type="button"
          // Hover/keyboard hint clarifying the start-over semantic (the visible
          // "change" text stays the accessible name). v1 starts a NEW game from
          // a revised prompt — it does not edit the current build in place.
          title="Start over — describe a new game to get a different genre"
          onClick={(e) => {
            e.stopPropagation();
            onChange();
          }}
          className="ml-1 cursor-pointer underline decoration-dotted underline-offset-2 opacity-80 hover:opacity-100"
        >
          change
        </button>
      ) : null}
    </span>
  );
}

/** Tooltip copy for the sandbox-floor branch, keyed on why the floor was used. */
function floorTitle(inference: ScaffoldFirstInference): string {
  const pct = Math.round(inference.confidence * 100);
  switch (inference.floorReason) {
    case 'below-threshold':
      return inference.candidate
        ? `Closest genre guess "${inference.candidate}" (${pct}%) was below the ` +
            `confidence threshold — building a generic sandbox you can shape live.`
        : `No genre cleared the confidence threshold — building a generic sandbox.`;
    case 'no-match':
      return `No genre matched your prompt — building a generic sandbox you can shape live.`;
    case 'error':
      return `Genre inference was unavailable — building a generic sandbox you can shape live.`;
    default:
      return `Building a generic sandbox you can shape live.`;
  }
}
